import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, MapPin, Calendar, Clock, Users, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  park: 'Park',
  court: 'Court',
  trail: 'Trail',
  field: 'Field',
  venue: 'Venue',
};

function deriveLiveState(act) {
  const status = act.lifecycle_status || 'idea';
  if (status === 'completed' || status === 'canceled') return status;
  const count = act.participant_count || 0;
  if (count >= 3) return 'good_to_go';
  return 'needs_people';
}

const STATE_STYLES = {
  good_to_go: { bg: '#D1FAE5', text: '#065F46', label: 'Good to go' },
  needs_people: { bg: '#FEF3C7', text: '#92400E', label: 'Needs people' },
  idea: { bg: '#F3F4F6', text: '#6B7280', label: 'Idea' },
};

export default function PlaceView({ anchor, onClose }) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!anchor) return;
    setLoading(true);
    const params = new URLSearchParams({
      lat: anchor.lat,
      lng: anchor.lng,
      name: anchor.name || '',
      radius: '0.008',
    });
    axios.get(`${API}/api/map/place?${params}`)
      .then(r => setActivities(r.data.activities || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [anchor?.lat, anchor?.lng, anchor?.name]);

  if (!anchor) return null;

  const typeLabel = TYPE_LABELS[anchor.type] || '';

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-[2000] animate-in slide-in-from-bottom"
      style={{ maxHeight: '55vh' }}
      data-testid="place-view"
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
    >
      <div
        className="rounded-t-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full" style={{ backgroundColor: 'var(--fy-border-subtle)' }} />
        </div>

        {/* Header */}
        <div className="px-4 pb-3 flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold" style={{ color: 'var(--fy-text-primary)' }} data-testid="place-name">
              {anchor.name}
            </h3>
            {typeLabel && (
              <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>{typeLabel}</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:opacity-70"
            style={{ color: 'var(--fy-text-disabled)' }}
            data-testid="place-view-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Activities list */}
        <div className="px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(55vh - 80px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
                Nothing happening here yet
              </p>
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set('location', anchor.name);
                  if (anchor.lat) params.set('lat', anchor.lat);
                  if (anchor.lng) params.set('lng', anchor.lng);
                  navigate(`/quick-create?${params}`);
                }}
                className="mt-3 px-4 py-2 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
                data-testid="place-start-something"
              >
                Start something here
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {activities.map(act => {
                const state = deriveLiveState(act);
                const stateStyle = STATE_STYLES[state] || STATE_STYLES.idea;
                return (
                  <button
                    key={act.activity_id}
                    onClick={() => navigate(`/activities/${act.activity_id}`)}
                    className="w-full text-left p-3 rounded-xl transition-all active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
                    data-testid={`place-activity-${act.activity_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>
                          {act.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                          {act.start_time && (
                            <span className="flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />
                              {formatTime(act.start_time)}
                            </span>
                          )}
                          {act.host_name && (
                            <span className="truncate">{act.host_name || act.created_by_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: stateStyle.bg, color: stateStyle.text }}
                        >
                          {stateStyle.label}
                        </span>
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
